import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
    type AuditLogsFilterInput,
    useGetAuditedCollectionsQuery,
    useGetAuditLogsPaginatedQuery,
    useSearchUsersForSelectLazyQuery,
} from 'data/__generated__';

import { DataTable } from 'components/DataTable';

import { type AuditRow, buildAuditColumns } from '../constants/auditColumns';

export const AuditList = () => {
    // Portée éventuelle venue d'un lien depuis une page de détail : /audit?collectionDocumentId=<id>,
    // depuis une page SANS document — le planning de livraison — : /audit?collectionName=<nom>, ou
    // depuis une ligne d'audit : /audit?requestId=<id>, qui montre TOUTE une action.
    const [searchParams] = useSearchParams();
    const collectionDocumentId = searchParams.get('collectionDocumentId');
    const collectionName = searchParams.get('collectionName');
    const requestId = searchParams.get('requestId');
    const scopedFilters: Partial<AuditLogsFilterInput> | undefined =
        collectionDocumentId || collectionName || requestId
            ? {
                  ...(collectionDocumentId ? { collectionDocumentId } : undefined),
                  // Le filtre est une LISTE — c'est un choix multiple côté table.
                  ...(collectionName ? { collectionName: [collectionName] } : undefined),
                  ...(requestId ? { requestId } : undefined),
              }
            : undefined;

    const { data: auditedCollectionsData } = useGetAuditedCollectionsQuery();
    const [searchUsers] = useSearchUsersForSelectLazyQuery();

    // Stable : le filtre async met cette fonction en dépendance de son effet de recherche.
    const loadAuthors = useCallback(
        async (search: string) => {
            const { data } = await searchUsers({ variables: { searchValue: search || null, limit: 20 } });

            return (data?.users.users ?? []).map((user) => ({
                value: user._id,
                label: user.profile?.fullname ? `${user.profile.fullname} (${user.username})` : user.username,
            }));
        },
        [searchUsers],
    );

    const columns = useMemo(
        () => buildAuditColumns({ collections: auditedCollectionsData?.auditedCollections ?? [], loadAuthors }),
        [auditedCollectionsData?.auditedCollections, loadAuthors],
    );

    const { loading, data, error, refetch, fetchMore } = useGetAuditLogsPaginatedQuery({
        // `filters`, `sortBy` et `sortOrder` sont dans les `keyArgs` de la field policy : les omettre ici alors
        // que le scroll infini les envoie ferait écrire `fetchMore` dans une AUTRE entrée de cache que celle
        // que lit le composant. Sans scope, la table renvoie `filters: {}`, pas `undefined`.
        variables: { filters: scopedFilters ?? {}, sortBy: 'createdAt', sortOrder: 'desc' },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    if (error) {
        throw new Error('Une erreur est survenue lors de la récupération de l’historique');
    }

    return (
        <div className="flex h-full w-full flex-col overflow-hidden">
            <DataTable<AuditRow, AuditLogsFilterInput>
                columns={columns}
                data={data?.auditLogsPaginated.auditLogs ?? []}
                filterMode="server"
                // Le scope du lien « voir plus » survit à « Effacer » et au rechargement.
                pinnedFilters={scopedFilters}
                getRowId={(row) => row._id}
                getRowLink={(row) => `/audit/${row._id}`}
                hasMore={data?.auditLogsPaginated.hasMore ?? false}
                isLoading={loading}
                refetch={refetch}
                fetchMore={fetchMore}
                defaultSortBy="createdAt"
                defaultSortOrder="desc"
                searchPlaceholder="Nom de l’auteur"
                searchFilterKey="searchValue"
            />
        </div>
    );
};
